import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

interface HeaderItem {
  id: string;
  component: React.ReactNode;
  priority: number; // Lower number = higher priority
  alwaysInDropdown?: boolean; // If true, always put in dropdown by default
}

interface UseResponsiveHeaderOptions {
  items: HeaderItem[];
  containerRef: React.RefObject<HTMLElement>;
  accountToggleRef?: React.RefObject<HTMLElement>;
  minItemWidth?: number;
  spacing?: number;
}

export const useResponsiveHeader = ({
  items,
  containerRef,
  accountToggleRef,
  minItemWidth = 120,
  spacing = 8
}: UseResponsiveHeaderOptions) => {
  const [visibleItems, setVisibleItems] = useState<HeaderItem[]>([]);
  const [hiddenItems, setHiddenItems] = useState<HeaderItem[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const hasInitialized = useRef(false);

  // Sort items by priority (lower number = higher priority) - memoize to prevent infinite re-renders
  const sortedItems = useMemo(() => [...items].sort((a, b) => a.priority - b.priority), [items]);

  const calculateVisibleItems = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const totalWidth = container.offsetWidth;
    const dropdownButtonWidth = 40; // Approximate width of three dots button with padding
    
    // Get actual account toggle width - this is the key measurement
    let accountToggleWidth = 0;
    if (accountToggleRef?.current) {
      accountToggleWidth = accountToggleRef.current.offsetWidth;
    } else {
      // Fallback: Account toggle section width varies by screen size
      const isSmallScreen = window.innerWidth < 1280; // xl breakpoint
      accountToggleWidth = isSmallScreen ? 120 : 180; // Fallback estimates
    }
    
    // Calculate available space: Total width - Account toggle width - minimum gap
    const minGap = 20; // Minimum gap between left items and Account toggle
    const usableWidth = totalWidth - accountToggleWidth - minGap;

    // Separate items into always-in-dropdown and regular items
    const alwaysInDropdownItems = sortedItems.filter(item => item.alwaysInDropdown);
    const regularItems = sortedItems.filter(item => !item.alwaysInDropdown);

    // Minimum usable width check - if we can't fit even one item, show dropdown
    if (usableWidth < minItemWidth) {
      setVisibleItems([]);
      setHiddenItems(sortedItems);
      setShowDropdown(true);
      return;
    }

    let currentWidth = 0;
    const visible: HeaderItem[] = [];
    const hidden: HeaderItem[] = [];

    // First, add items that are marked as always in dropdown to hidden
    hidden.push(...alwaysInDropdownItems);

    // Then process regular items based on available space
    for (const item of regularItems) {
      const itemWidth = minItemWidth + spacing;
      
      // Check if we can fit this item plus the dropdown button if needed
      const remainingSpace = usableWidth - currentWidth;
      if (remainingSpace >= itemWidth) {
        visible.push(item);
        currentWidth += itemWidth;
      } else {
        hidden.push(item);
      }
    }

    // Show dropdown if we have hidden items OR if we can't fit the dropdown button
    const needsDropdown = hidden.length > 0 || (visible.length > 0 && currentWidth + dropdownButtonWidth > usableWidth);
    
    if (needsDropdown) {
      // If we can't fit the dropdown button, move items to hidden until we can
      while (visible.length > 0 && currentWidth + dropdownButtonWidth > usableWidth) {
        const lastVisible = visible.pop()!;
        hidden.unshift(lastVisible);
        currentWidth -= (minItemWidth + spacing);
      }
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }

    setVisibleItems(visible);
    setHiddenItems(hidden);
  }, [sortedItems, minItemWidth, spacing, accountToggleRef]);

  // Resize observer to detect container size changes
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  // Window resize listener to detect screen size changes (for responsive breakpoints)
  useEffect(() => {
    const handleWindowResize = () => {
      calculateVisibleItems();
    };

    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [calculateVisibleItems]);

  // Recalculate when container width changes
  useEffect(() => {
    calculateVisibleItems();
  }, [containerWidth, calculateVisibleItems]);

  // Initial calculation - only run once on mount
  useEffect(() => {
    if (!hasInitialized.current) {
      calculateVisibleItems();
      hasInitialized.current = true;
    }
  }, [calculateVisibleItems]);

  return {
    visibleItems,
    hiddenItems,
    showDropdown,
    containerWidth
  };
};
